import { Control } from 'react-hook-form'
import { CustomCheckboxInput, QuestionCard, SimpleText, YStack } from '@my/ui'

import { DropdownField, InputField } from '../helpers/Helper'
import images from '../../../assets/images'
import {
  April2026UpdatedOptions,
  climateImpactOptions,
  financialAdviceOptions,
  investmentAmountOptions,
  investmentLengthOptions,
  riskToleranceOptions,
} from '../data'
import { color } from '@my/util'

interface RiskToleranceProps {
  control: Control<any>
}
export const RiskTolerance: React.FC<RiskToleranceProps> = ({ control }) => {
  return (
    <>
      <YStack width={'100%'} pl={0}>
        <YStack
          borderWidth={1}
          borderRadius={10}
          borderColor={'rgba(133, 193, 157, 0.17)'}
          paddingVertical={20}
          paddingHorizontal={15}
          bg={'#F9F9F9'}
        >
          <SimpleText fontSize={16} fontWeight={'600'}>
            Investment recommendations are based on the entirety of your financial profile, including the questions below.
          </SimpleText>
        </YStack>
      </YStack>
      {/* Investment Length Question */}
      <QuestionCard infoButton questionGroupTitle="How many years do you plan to keep your money invested?">
        <CustomCheckboxInput
          options={investmentLengthOptions}
          control={control}
          name="Risk & Investment Prefs - Investment Length"
          circle
          optionDescriptions
          label={`Time horizon is one of the most critical factors in investment strategy. Longer time horizons generally allow for greater exposure to stocks and higher-risk investments because there's time to recover from market downturns. Shorter time horizons typically require more conservative strategies to protect principal. Historical data shows that stocks have outperformed bonds over periods of 10+ years, but can be volatile in the short term.`}
        />
      </QuestionCard>

      {/* Risk Tolerance Question */}
      <QuestionCard
        infoButton questionGroupTitle={`On a scale from 1 to 5, with 1 being very conservative (low risk) and 5 being very aggressive (high risk), how would you rate your tolerance for investment risk?`}
      >
        <CustomCheckboxInput
          options={riskToleranceOptions}
          control={control}
          name="Risk & Investment Prefs - Investment Risk Tolerance"
          circle
          optionDescriptions
          label={`Your behavioral response to losses is crucial. Market declines are normal—the S&P 500 has experienced a 10%+ decline in about half of all years historically, yet has delivered positive long-term returns. If you're likely to sell during downturns, you'll lock in losses and potentially miss recoveries. We need to structure a portfolio you can stick with through market cycles. Research shows that staying invested through volatility is critical to long-term success. Read each of the answers below to best assess what you would do if your portfolio lost 20% of its value:`}
        />
      </QuestionCard>

      {/* Climate Impact Concern */}
      <QuestionCard
        infoButton questionGroupTitle={`On a scale from 1 to 5, with 1 being not concerned about climate impact and 5 being extremely concerned, how would you rate your concern regarding climate change's impact on your investment?`}
      >
        <CustomCheckboxInput
          options={climateImpactOptions}
          control={control}
          name="Risk & Investment Prefs - Investment Climate Impact Concern"
          circle
          optionDescriptions
          label={`Many investors want their investments to reflect their values. Our Climate Transition portfolio specifically focuses on supporting the shift to a low-carbon economy. While values-based investing may involve some screening that could affect diversification, research increasingly shows that strong environmental practices can correlate with good long-term performance.`}
        />
      </QuestionCard>

      {/* Planned Investment Amount */}
      <QuestionCard infoButton questionGroupTitle="How much are you planning to invest (into Zillennial Advisors curated model portfolio)?">
        <CustomCheckboxInput
          options={investmentAmountOptions}
          control={control}
          name="Risk & Investment Prefs - Planned Investment Amount"
          circle
          label={`We offer a low-cost portfolio option designed specifically for investors starting with smaller amounts. This portfolio features low fees and no management fee, making it cost-effective for building your initial investment. As your account grows, you'll have the flexibility to transition to other portfolio options with additional features and active management if desired.`}
        />
      </QuestionCard>

      <QuestionCard questionGroupTitle="How much are you able to invest per month (in an ongoing basis) after all expenses and debt payments?">
        <DropdownField
          control={control}
          name="Risk & Investment Prefs - Monthly Investment Capacity"
          items={April2026UpdatedOptions['Risk & Investment'].monthlyInvestmentCapacity}
          native
        />
      </QuestionCard>
    </>
  )
}
